import { useState } from 'react';
import type { ApprovalView } from '@alfred/shared';
import { api, describeError } from './api.js';

const TOOL_LABELS: Record<string, string> = {
  'x-post': 'New X post (your own)',
  email: 'Send email',
  content: 'Content watchlist',
  budget: 'Budget',
  reddit: 'Reddit',
  video: 'Video',
  calendar: 'Calendar',
  files: 'Files',
  'web-search': 'Web search',
  memory: 'Memory',
  upgrades: 'Upgrade Alfred',
};

/** X's limit; links count as 23 and emoji as 2, which the server checks exactly. */
const X_LIMIT = 280;

/** Drafted actions waiting for Anthony. Approve runs the exact input shown, once. */
export function Approvals({
  items,
  onChanged,
  onNotice,
  title = 'Waiting for you',
}: {
  items: ApprovalView[];
  onChanged: () => Promise<void>;
  onNotice: (text: string) => void;
  title?: string;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  if (items.length === 0) return null;

  const run = async (item: ApprovalView, action: () => Promise<ApprovalView>) => {
    setBusy(item.id);
    try {
      const res = await action();
      onNotice(
        res.status === 'executed'
          ? `Done: ${res.summary}`
          : res.status === 'rejected'
            ? `Rejected: ${res.summary}`
            : `Didn’t work: ${res.error ?? 'unknown error'}`,
      );
      return true;
    } catch (err) {
      onNotice(describeError(err));
      return false;
    } finally {
      setBusy(null);
      await onChanged();
    }
  };

  return (
    <section className="approvals" aria-label="Waiting for your approval">
      <h2>
        {title} ({items.length})
      </h2>
      {items.map((item) => {
        const reddit = redditDraft(item);
        const video = videoDraft(item);
        if (video) {
          return <VideoCard key={item.id} item={item} draft={video} busy={busy} run={run} />;
        }
        return reddit ? (
          <RedditCard key={item.id} item={item} draft={reddit} busy={busy} run={run} />
        ) : (
          <ApprovalCard key={item.id} item={item} busy={busy} run={run} />
        );
      })}
    </section>
  );
}

function ApprovalCard({
  item,
  busy,
  run,
}: {
  item: ApprovalView;
  busy: string | null;
  run: (item: ApprovalView, action: () => Promise<ApprovalView>) => Promise<boolean>;
}) {
  const posts = xPosts(item);
  const email = emailDraft(item);
  // Rewording a draft before approving it: Alfred learns Anthony's style from the difference.
  const [editing, setEditing] = useState<string[] | null>(null);
  const [emailEdit, setEmailEdit] = useState<{ subject: string; body: string } | null>(null);
  // Rejecting, with an optional reason (also something Alfred learns from).
  const [reason, setReason] = useState<string | null>(null);
  const pending = item.status === 'pending';
  const disabled = busy !== null;
  const postsChanged = editing !== null && posts !== null && editing.some((p, i) => p !== posts[i]);
  const emailChanged =
    emailEdit !== null &&
    email !== null &&
    (emailEdit.subject !== email.subject || emailEdit.body !== email.body);
  const changed = postsChanged || emailChanged;
  const isDraft = Boolean(posts ?? email);

  const approve = () =>
    void run(item, () =>
      api.approve(
        item.id,
        postsChanged && editing
          ? { ...item.input, posts: editing }
          : emailChanged && emailEdit
            ? { ...item.input, ...emailEdit }
            : undefined,
      ),
    );
  const reject = () => void run(item, () => api.reject(item.id, reason?.trim() || undefined));

  return (
    <article className="approval" id={`draft-${item.id}`}>
      <div className="approval-tool">{TOOL_LABELS[item.toolName] ?? item.toolName}</div>

      {emailEdit && email ? (
        <div className="approval-email">
          <div className="muted small">To: {email.to}</div>
          <input
            className="reject-reason"
            value={emailEdit.subject}
            aria-label="Subject"
            onChange={(e) => setEmailEdit({ ...emailEdit, subject: e.target.value })}
          />
          <label className="post-edit">
            <textarea
              value={emailEdit.body}
              rows={Math.min(14, Math.max(5, emailEdit.body.split('\n').length + 1))}
              aria-label="Email text"
              onChange={(e) => setEmailEdit({ ...emailEdit, body: e.target.value })}
            />
          </label>
        </div>
      ) : editing ? (
        <div className="approval-posts">
          {editing.map((text, i) => (
            <label key={i} className="post-edit">
              <textarea
                value={text}
                rows={Math.min(8, Math.max(3, Math.ceil(text.length / 48)))}
                aria-label={editing.length > 1 ? `Post ${i + 1} of ${editing.length}` : 'Post text'}
                onChange={(e) => setEditing(editing.map((p, j) => (j === i ? e.target.value : p)))}
              />
              <span className={text.length > X_LIMIT ? 'count count-over' : 'count'}>
                {text.length}/{X_LIMIT}
              </span>
            </label>
          ))}
        </div>
      ) : (
        <Preview item={item} posts={posts} email={email} />
      )}

      {reason !== null && (
        <input
          className="reject-reason"
          value={reason}
          maxLength={500}
          placeholder="Why not? (optional, helps Alfred learn)"
          aria-label="Reason for rejecting"
          autoFocus
          onChange={(e) => setReason(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && reject()}
        />
      )}

      <div className="approval-actions">
        {reason !== null ? (
          <>
            <button className="btn btn-danger btn-small" disabled={disabled} onClick={reject}>
              {busy === item.id ? 'Working…' : 'Reject'}
            </button>
            <button className="btn btn-small" disabled={disabled} onClick={() => setReason(null)}>
              Cancel
            </button>
          </>
        ) : (
          <>
            <button className="btn btn-primary btn-small" disabled={disabled} onClick={approve}>
              {busy === item.id
                ? 'Working…'
                : item.status === 'approved'
                  ? 'Retry'
                  : changed
                    ? 'Approve edited'
                    : 'Approve'}
            </button>
            {posts && pending && !editing && (
              <button
                className="btn btn-small"
                disabled={disabled}
                onClick={() => setEditing([...posts])}
              >
                Edit
              </button>
            )}
            {email && pending && !emailEdit && (
              <button
                className="btn btn-small"
                disabled={disabled}
                onClick={() => setEmailEdit({ subject: email.subject, body: email.body })}
              >
                Edit
              </button>
            )}
            {(editing ?? emailEdit) && (
              <button
                className="btn btn-small"
                disabled={disabled}
                onClick={() => {
                  setEditing(null);
                  setEmailEdit(null);
                }}
              >
                Undo edits
              </button>
            )}
            <button
              className="btn btn-small"
              disabled={disabled || !pending}
              onClick={() => (isDraft ? setReason('') : reject())}
            >
              Reject
            </button>
          </>
        )}
      </div>
    </article>
  );
}

function xPosts(item: ApprovalView): string[] | null {
  const posts = item.toolName === 'x-post' ? (item.input.posts as unknown) : null;
  return Array.isArray(posts) && posts.every((p) => typeof p === 'string') ? posts : null;
}

/** A proposed content watchlist, grouped for reading ("TikTok hashtags: …"). */
function watchlistDraft(item: ApprovalView) {
  const i = item.input as Record<string, unknown>;
  if (item.toolName !== 'content' || i.op !== 'set-watchlist' || !Array.isArray(i.items))
    return null;
  const groups = new Map<string, string[]>();
  const names = { tiktok: 'TikTok', instagram: 'Instagram' } as Record<string, string>;
  const kinds = { hashtag: 'hashtags', search: 'searches', creator: 'creators' } as Record<
    string,
    string
  >;
  for (const raw of i.items) {
    const x = raw as { platform?: string; type?: string; value?: string };
    if (!x.platform || !x.type || !x.value) continue;
    const label = `${names[x.platform] ?? x.platform} ${kinds[x.type] ?? x.type}`;
    const prefix = x.type === 'hashtag' ? '#' : x.type === 'creator' ? '@' : '';
    groups.set(label, [...(groups.get(label) ?? []), `${prefix}${x.value}`]);
  }
  return {
    niche: typeof i.niche === 'string' ? i.niche : '',
    groups: [...groups].map(([label, values]) => ({ label, values })),
  };
}

interface EmailDraft {
  to: string;
  cc: string;
  subject: string;
  body: string;
}

function emailDraft(item: ApprovalView): EmailDraft | null {
  const i = item.input as Record<string, unknown>;
  if (item.toolName !== 'email' || i.op !== 'send') return null;
  const list = (v: unknown) =>
    Array.isArray(v) ? v.filter((x) => typeof x === 'string').join(', ') : '';
  return {
    to: list(i.to),
    cc: list(i.cc),
    subject: typeof i.subject === 'string' ? i.subject : '',
    body: typeof i.body === 'string' ? i.body : '',
  };
}

/** Shows exactly what will happen: post text for X, the email for email, otherwise the summary plus raw input. */
function Preview({
  item,
  posts,
  email,
}: {
  item: ApprovalView;
  posts: string[] | null;
  email: EmailDraft | null;
}) {
  const upgrade = upgradeDraft(item);
  if (upgrade) {
    return (
      <div className="approval-email upgrade-spec">
        <p>
          <strong>{upgrade.title}</strong>
        </p>
        <div className="small">
          <span className="muted">Why:</span> {upgrade.why}
        </div>
        <div className="small">
          <span className="muted">What changes:</span> {upgrade.what}
        </div>
        {upgrade.areas.length > 0 && (
          <div className="small">
            <span className="muted">Touches:</span> {upgrade.areas.join(', ')}
          </div>
        )}
        <div className="small">
          <span className="muted">How to tell it works:</span> {upgrade.checks}
        </div>
        <p className="muted small">
          Approving queues it. It gets built in Claude Code on your PC (say “do Alfred’s approved
          upgrades”), where you can watch, then deployed.
        </p>
      </div>
    );
  }
  const watch = watchlistDraft(item);
  if (watch) {
    return (
      <div className="approval-email">
        <div className="small">
          <span className="muted">Niche:</span> {watch.niche}
        </div>
        {watch.groups.map((g) => (
          <div key={g.label} className="small">
            <span className="muted">{g.label}:</span> {g.values.join(', ')}
          </div>
        ))}
      </div>
    );
  }
  if (email) {
    return (
      <div className="approval-email">
        <div className="small">
          <span className="muted">To:</span> {email.to}
        </div>
        {email.cc && (
          <div className="small">
            <span className="muted">Cc:</span> {email.cc}
          </div>
        )}
        <div className="small">
          <span className="muted">Subject:</span> <strong>{email.subject}</strong>
        </div>
        <blockquote>{email.body}</blockquote>
      </div>
    );
  }
  if (posts) {
    return (
      <div className="approval-posts">
        <InspirationLink item={item} />
        {posts.map((p, i) => (
          <blockquote key={i}>{p}</blockquote>
        ))}
      </div>
    );
  }
  return (
    <>
      <p className="approval-summary">{item.summary}</p>
      <details>
        <summary>Details</summary>
        <pre>{JSON.stringify(item.input, null, 2)}</pre>
      </details>
    </>
  );
}

/** The winning post an X draft borrows its format from (Alfred's X trends). */
function inspiration(
  item: ApprovalView,
): { url: string; label: string; numbers: string | null; why: string | null } | null {
  const i = (item.input as { inspiration?: Record<string, unknown> }).inspiration;
  if (!i || typeof i.url !== 'string' || !/^https:\/\//.test(i.url)) return null;
  const n = (v: unknown) => (typeof v === 'number' ? v.toLocaleString() : null);
  const parts = [
    n(i.likes) && `${n(i.likes)} likes`,
    n(i.views) && `${n(i.views)} views`,
    typeof i.outlier === 'number' && `${i.outlier}× their usual`,
  ].filter(Boolean);
  return {
    url: i.url,
    label:
      typeof i.label === 'string' ? i.label : `@${typeof i.author === 'string' ? i.author : '?'}`,
    numbers: typeof i.numbers === 'string' ? i.numbers : parts.length ? parts.join(' · ') : null,
    why:
      typeof i.why === 'string' && i.why
        ? i.why
        : typeof i.title === 'string'
          ? `“${i.title}”`
          : null,
  };
}

/** "Copying this": a link to the post a draft borrows its format from. */
function InspirationLink({ item }: { item: ApprovalView }) {
  const insp = inspiration(item);
  if (!insp) return null;
  return (
    <div className="inspiration small">
      <span className="muted">Copying the format of </span>
      <a href={insp.url} target="_blank" rel="noreferrer">
        {insp.label} ↗
      </a>
      {insp.numbers && <span className="muted"> · {insp.numbers}</span>}
      {insp.why && <div className="muted">{insp.why}</div>}
    </div>
  );
}

interface UpgradeDraft {
  title: string;
  why: string;
  what: string;
  areas: string[];
  checks: string;
}

function upgradeDraft(item: ApprovalView): UpgradeDraft | null {
  const i = item.input as Record<string, unknown>;
  if (item.toolName !== 'upgrades' || i.op !== 'propose') return null;
  const str = (v: unknown) => (typeof v === 'string' ? v : '');
  return {
    title: str(i.title),
    why: str(i.why),
    what: str(i.what),
    areas: Array.isArray(i.areas) ? i.areas.filter((x) => typeof x === 'string') : [],
    checks: str(i.checks),
  };
}

interface RedditDraft {
  platform: 'reddit' | 'x';
  kind: 'reply' | 'post';
  subreddit: string;
  threadTitle: string;
  threadUrl: string;
  text: string;
}

function redditDraft(item: ApprovalView): RedditDraft | null {
  const i = item.input as Record<string, unknown>;
  const str = (v: unknown) => (typeof v === 'string' ? v : '');
  // An X reply Anthony posts himself works the same way: copy, open the post, post it.
  if (item.toolName === 'x-trends' && i.op === 'replied') {
    return {
      platform: 'x',
      kind: 'reply',
      subreddit: '',
      threadTitle: `@${str(i.author)}: ${str(i.replyToText).slice(0, 200)}`,
      threadUrl: str(i.replyToUrl),
      text: str(i.text),
    };
  }
  if (item.toolName !== 'reddit' || i.op !== 'posted') return null;
  return {
    platform: 'reddit',
    kind: i.kind === 'post' ? 'post' : 'reply',
    subreddit: str(i.subreddit),
    threadTitle: str(i.threadTitle),
    threadUrl: str(i.threadUrl),
    text: str(i.text),
  };
}

/**
 * A drafted Reddit reply or post. Alfred never posts to Reddit: Anthony copies the text, opens
 * the thread, posts it himself, then taps "I posted it" (or Skip).
 */
function RedditCard({
  item,
  draft,
  busy,
  run,
}: {
  item: ApprovalView;
  draft: RedditDraft;
  busy: string | null;
  run: (item: ApprovalView, action: () => Promise<ApprovalView>) => Promise<boolean>;
}) {
  const [text, setText] = useState(draft.text);
  const [copied, setCopied] = useState(false);
  const disabled = busy !== null;
  const changed = text !== draft.text;
  const safeUrl = /^https:\/\/((www\.)?reddit\.com|x\.com|twitter\.com)\//.test(draft.threadUrl)
    ? draft.threadUrl
    : null;
  const isX = draft.platform === 'x';

  const copy = () =>
    void navigator.clipboard?.writeText(text).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      },
      () => setCopied(false),
    );

  return (
    <article className="approval" id={`draft-${item.id}`}>
      <div className="approval-tool">
        {isX
          ? 'X reply (you post it)'
          : `Reddit ${draft.kind === 'post' ? 'post (your own)' : 'reply'} · r/${draft.subreddit}`}
      </div>
      <InspirationLink item={item} />
      <div className="small">
        {draft.kind === 'post' ? 'Title: ' : 'Replying to: '}
        <strong>{draft.threadTitle}</strong>
      </div>
      <label className="post-edit">
        <textarea
          value={text}
          rows={Math.min(12, Math.max(4, Math.ceil(text.length / 60)))}
          aria-label="Draft text"
          onChange={(e) => setText(e.target.value)}
        />
      </label>
      <div className="approval-actions">
        <button className="btn btn-small" onClick={copy}>
          {copied ? 'Copied ✓' : 'Copy'}
        </button>
        {safeUrl && (
          <a className="btn btn-small" href={safeUrl} target="_blank" rel="noopener noreferrer">
            {isX ? 'Open post' : draft.kind === 'post' ? 'Open subreddit' : 'Open thread'}
          </a>
        )}
        <button
          className="btn btn-primary btn-small"
          disabled={disabled}
          onClick={() =>
            void run(item, () =>
              api.approve(item.id, changed ? { ...item.input, text } : undefined),
            )
          }
        >
          {busy === item.id ? 'Working…' : 'I posted it'}
        </button>
        <button
          className="btn btn-small"
          disabled={disabled}
          onClick={() => void run(item, () => api.reject(item.id))}
        >
          Skip
        </button>
      </div>
    </article>
  );
}

interface VideoDraft {
  project: string;
  productionId: string;
  title: string;
  caption: string;
}

function videoDraft(item: ApprovalView): VideoDraft | null {
  const i = item.input as Record<string, unknown>;
  if (item.toolName !== 'video' || i.op !== 'posted') return null;
  const str = (v: unknown) => (typeof v === 'string' ? v : '');
  if (!str(i.project) || !str(i.productionId)) return null;
  return {
    project: str(i.project),
    productionId: str(i.productionId),
    title: str(i.title),
    caption: str(i.caption),
  };
}

/** A finished video: watch it, copy the caption, download it, post it yourself. */
function VideoCard({
  item,
  draft,
  busy,
  run,
}: {
  item: ApprovalView;
  draft: VideoDraft;
  busy: string | null;
  run: (item: ApprovalView, action: () => Promise<ApprovalView>) => Promise<boolean>;
}) {
  const [copied, setCopied] = useState(false);
  const src = api.videoFileUrl(draft.project, draft.productionId);
  const disabled = busy !== null;
  return (
    <article className="approval" id={`draft-${item.id}`}>
      <div className="approval-tool">Video · {draft.title}</div>
      <InspirationLink item={item} />
      <video className="approval-video" src={src} controls playsInline preload="metadata" />
      {draft.caption && <blockquote>{draft.caption}</blockquote>}
      <div className="approval-actions">
        {draft.caption && (
          <button
            className="btn btn-small"
            onClick={() =>
              void navigator.clipboard?.writeText(draft.caption).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              })
            }
          >
            {copied ? 'Copied ✓' : 'Copy caption'}
          </button>
        )}
        <a className="btn btn-small" href={src} download={`${draft.title || 'video'}.mp4`}>
          Download
        </a>
        <button
          className="btn btn-primary btn-small"
          disabled={disabled}
          onClick={() => void run(item, () => api.approve(item.id))}
        >
          {busy === item.id ? 'Working…' : 'I posted it'}
        </button>
        <button
          className="btn btn-small"
          disabled={disabled}
          onClick={() => void run(item, () => api.reject(item.id))}
        >
          Skip
        </button>
      </div>
    </article>
  );
}
